// Constants
import { ROLE_CODES } from '../constants/userRoles';

/**
 * Filters office options based on user permissions and roles, and determines default office selection.
 *
 * This utility encapsulates all business logic for office filtering and default selection.
 * Consumers should simply follow the returned values without implementing any role-based logic.
 *
 * BUSINESS RULES:
 * ================
 * 1. Default office should ALWAYS be auto-selected regardless of user role
 * 2. C_LEVEL users: See ALL offices from master list
 * 3. Non-C_LEVEL users: See ONLY offices they are assigned to
 * 4. Default office determination (in priority order):
 *    a. Office marked as default (isDefault: true) in officeAssignments
 *    b. If user has exactly ONE office assignment, use that office
 *    c. If no office assignments exist but officeIds are present, use the FIRST officeId
 *    d. Otherwise, null
 *
 * @param {Array} masterOfficeList - Array of all available offices from the master list
 *                                   Structure: [{ id, name, countryName, ... }]
 * @param {Array} userRoles - Array of user's global roles
 *                            Structure: [{ id, roleName, roleTitle }]
 * @param {Array} userOfficeAssignments - Array of user's office assignments
 *                                        Structure: [{ isDefault, office: { id, name }, officeRoles: [...] }]
 * @param {Array} userOfficeIds - Array of office IDs from user's authorization data
 *                                Structure: [officeId1, officeId2, ...]
 *
 * @returns {Object} Object containing filtered offices, default office ID, and selection behavior
 *   {
 *     filteredOffices: Array<Object> - Offices the user is allowed to see
 *     defaultOfficeId: string|null - ID of the office to be selected by default
 *     shouldAutoSelectDefault: boolean - Always true (default should always be auto-selected)
 *   }
 *
 * @example
 * // C_LEVEL user with default office
 * filterOfficesByUserPermissions(
 *   allOffices,
 *   [{ roleName: 'C_LEVEL' }],
 *   [{ isDefault: true, office: { id: '123' } }],
 *   ['123', '456']
 * )
 * // Returns: { filteredOffices: allOffices, defaultOfficeId: '123', shouldAutoSelectDefault: true }
 *
 * @example
 * // Regular user with one office assignment
 * filterOfficesByUserPermissions(
 *   allOffices,
 *   [{ roleName: 'MANAGER' }],
 *   [{ isDefault: false, office: { id: '456' } }],
 *   ['456']
 * )
 * // Returns: { filteredOffices: [office456], defaultOfficeId: '456', shouldAutoSelectDefault: true }
 */
export const filterOfficesByUserPermissions = (
  masterOfficeList = [],
  userRoles = [],
  userOfficeAssignments = [],
  userOfficeIds = []
) => {
  // ====================================================================
  // STEP 1: Determine which offices the user is allowed to see
  // ====================================================================

  // Check if user has C_LEVEL role (C_LEVEL users can see all offices)
  const hasCLevelRole = userRoles.some(
    (role) => role.roleName === ROLE_CODES.C_LEVEL
  );

  let filteredOffices = [];

  if (hasCLevelRole) {
    // C_LEVEL users see ALL offices from master list
    filteredOffices = masterOfficeList;
  } else {
    // Non-C_LEVEL users see only offices they are assigned to
    // Extract office IDs from user's office assignments
    const userOfficeIdsFromAssignments = userOfficeAssignments.map(
      (assignment) => assignment.office.id
    );

    // Filter master office list to only include offices user is assigned to
    filteredOffices = masterOfficeList.filter((office) =>
      userOfficeIdsFromAssignments.includes(office.id)
    );
  }

  // ====================================================================
  // STEP 2: Determine the default office to be auto-selected
  // ====================================================================

  let defaultOfficeId = null;

  // Priority 1: Look for office marked as default in office assignments
  const defaultOfficeAssignment = userOfficeAssignments.find(
    (assignment) => assignment.isDefault === true
  );

  if (defaultOfficeAssignment?.office?.id) {
    defaultOfficeId = defaultOfficeAssignment.office.id;
  }
  // Priority 2: If user has exactly ONE office assignment, use that as default
  else if (userOfficeAssignments.length === 1) {
    defaultOfficeId = userOfficeAssignments[0].office.id;
  }
  // Priority 3: If no office assignments but officeIds exist, use the FIRST one
  else if (userOfficeIds.length > 0) {
    defaultOfficeId = userOfficeIds[0];
  }

  // ====================================================================
  // STEP 3: Return filtered offices and default selection instructions
  // ====================================================================

  return {
    filteredOffices,
    defaultOfficeId,
    shouldAutoSelectDefault: true, // Always true - default should always be selected
  };
};
